import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import { useApp } from '../../context/AppContext';
import { meses, anos } from '../../data/mockData';
import { uploadFotoCorretor, deleteFotoCorretor } from '../../services/supabaseService';
import { getImageUrl, validateImageFile } from '../../utils/imageUtils';

// Note: meses and anos are still used for UI, not for data storage

function ListaCorretores() {
  const { corretores, adicionarCorretor, atualizarCorretor, removerCorretor, loading, error } = useApp();
  
  // Debug: Log corretores received
  useEffect(() => {
    console.log('ListaCorretores - corretores recebidos:', corretores?.length || 0);
    console.log('ListaCorretores - loading:', loading);
    console.log('ListaCorretores - error:', error);
    if (corretores && corretores.length > 0) {
      console.log('ListaCorretores - primeiro corretor:', corretores[0]);
    }
  }, [corretores, loading, error]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [corretorSelecionado, setCorretorSelecionado] = useState(null);
  const [novoCorretor, setNovoCorretor] = useState({
    nome: '',
    email: '',
    telefone: '',
    creci: '',
    foto: '',
    ativo: true,
  });

  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [saving, setSaving] = useState(false); // Loading state for save operations
  const [deleting, setDeleting] = useState(false); // Loading state for delete operations

  const handleAdicionarCorretor = async () => {
    if (novoCorretor.nome && novoCorretor.email) {
      try {
        setSaving(true);
        setUploadingFoto(true);
        
        // Upload foto if it's a File object - SEMPRE fazer upload para Storage
        let fotoUrl = null;
        if (novoCorretor.foto instanceof File) {
          try {
            fotoUrl = await uploadFotoCorretor(novoCorretor.foto, null);
            console.log('Foto enviada para Storage, URL:', fotoUrl);
          } catch (uploadError) {
            console.error('Erro ao fazer upload da foto:', uploadError);
            const errorMsg = uploadError.message || 'Erro desconhecido';
            if (errorMsg.includes('Bucket')) {
              alert('Bucket de fotos não encontrado. Por favor, crie o bucket "fotos-corretores" no Supabase Storage antes de adicionar fotos.');
            } else {
              alert(`Erro ao fazer upload da foto: ${errorMsg}. O corretor será criado sem foto.`);
            }
            // Continue without photo if upload fails
            fotoUrl = null;
          }
        } else if (typeof novoCorretor.foto === 'string' && novoCorretor.foto.startsWith('http')) {
          // If it's already a URL (from external source), use it
          fotoUrl = novoCorretor.foto;
        }
        // NUNCA salvar base64 - apenas URLs
        
        // Cleanup preview URL
        if (novoCorretor.fotoPreview) {
          URL.revokeObjectURL(novoCorretor.fotoPreview);
        }
        
        await adicionarCorretor({
          nome: novoCorretor.nome,
          email: novoCorretor.email,
          telefone: novoCorretor.telefone || null,
          creci: novoCorretor.creci || null,
          foto: fotoUrl, // Apenas URL ou null - nunca base64
          ativo: novoCorretor.ativo,
        });
        
        setNovoCorretor({
          nome: '',
          email: '',
          telefone: '',
          creci: '',
          foto: null,
          fotoPreview: null,
          ativo: true,
        });
        setOpenDialog(false);
      } catch (error) {
        console.error('Error adding corretor:', error);
        alert('Erro ao adicionar corretor. Tente novamente.');
      } finally {
        setSaving(false);
        setUploadingFoto(false);
      }
    }
  };

  const handleEditarCorretor = (corretor) => {
    // Cleanup previous preview if exists
    if (novoCorretor.fotoPreview) {
      URL.revokeObjectURL(novoCorretor.fotoPreview);
    }
    
    setCorretorSelecionado(corretor);
    setNovoCorretor({
      nome: corretor.nome,
      email: corretor.email,
      telefone: corretor.telefone || '',
      creci: corretor.creci || '',
      foto: corretor.foto || null, // Keep existing URL or base64
      fotoPreview: null, // No preview for existing photos
      ativo: corretor.ativo,
    });
    setOpenEditDialog(true);
  };

  const handleSalvarEdicao = async () => {
    if (novoCorretor.nome && novoCorretor.email && corretorSelecionado) {
      try {
        setSaving(true);
        setUploadingFoto(true);
        
        let fotoUrl = corretorSelecionado.foto || null; // Keep existing photo by default
        
        // If removing photo (foto is explicitly null or empty)
        if (novoCorretor.foto === null || novoCorretor.foto === '') {
          // Delete old photo if it exists and is a storage URL
          if (corretorSelecionado.foto) {
            await deleteFotoCorretor(corretorSelecionado.foto);
          }
          fotoUrl = null;
        } else if (novoCorretor.foto instanceof File) {
          // Upload new photo
          try {
            // Delete old photo if it exists and is a storage URL
            if (corretorSelecionado.foto) {
              await deleteFotoCorretor(corretorSelecionado.foto);
            }
            fotoUrl = await uploadFotoCorretor(novoCorretor.foto, corretorSelecionado.id);
          } catch (uploadError) {
            console.error('Erro ao fazer upload da foto:', uploadError);
            alert('Erro ao fazer upload da foto. A foto antiga será mantida.');
            // Keep old photo if upload fails
            fotoUrl = corretorSelecionado.foto;
          }
        } else if (typeof novoCorretor.foto === 'string') {
          // If it's already a URL (http/https), use it
          if (novoCorretor.foto.startsWith('http')) {
            fotoUrl = novoCorretor.foto;
          }
          // Se for base64, NÃO salvar - manter foto existente ou null
          // Base64 antigo será mantido apenas para exibição, mas não será salvo novamente
          if (novoCorretor.foto.startsWith('data:')) {
            console.warn('Tentativa de salvar base64 ignorada. Use upload de arquivo para salvar nova foto.');
            // Manter foto existente se houver, senão null
            fotoUrl = corretorSelecionado.foto && corretorSelecionado.foto.startsWith('http') 
              ? corretorSelecionado.foto 
              : null;
          }
        }
        
        // Cleanup preview URL
        if (novoCorretor.fotoPreview) {
          URL.revokeObjectURL(novoCorretor.fotoPreview);
        }
        
        await atualizarCorretor(corretorSelecionado.id, {
          nome: novoCorretor.nome,
          email: novoCorretor.email,
          telefone: novoCorretor.telefone || null,
          creci: novoCorretor.creci || null,
          foto: fotoUrl,
          ativo: novoCorretor.ativo,
        });
        
        setOpenEditDialog(false);
        setCorretorSelecionado(null);
        setNovoCorretor({
          nome: '',
          email: '',
          telefone: '',
          creci: '',
          foto: null,
          fotoPreview: null,
          ativo: true,
        });
      } catch (error) {
        console.error('Error updating corretor:', error);
        alert('Erro ao atualizar corretor. Tente novamente.');
      } finally {
        setSaving(false);
        setUploadingFoto(false);
      }
    }
  };

  const handleConfirmarRemover = (corretor) => {
    setCorretorSelecionado(corretor);
    setOpenDeleteDialog(true);
  };

  const handleRemoverCorretor = async () => {
    if (corretorSelecionado) {
      try {
        setDeleting(true);
        
        // Delete foto from storage if it exists and is a storage URL
        if (corretorSelecionado.foto) {
          await deleteFotoCorretor(corretorSelecionado.foto);
        }
        
        await removerCorretor(corretorSelecionado.id);
        setOpenDeleteDialog(false);
        setCorretorSelecionado(null);
      } catch (error) {
        console.error('Error deleting corretor:', error);
        alert('Erro ao remover corretor. Tente novamente.');
      } finally {
        setDeleting(false);
      }
    }
  };

  // Debug: Show loading and error states
  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Carregando corretores...
        </Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="error" sx={{ mb: 2 }}>
          Erro ao carregar corretores: {error}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Verifique o console do navegador para mais detalhes.
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  const totalCorretores = corretores?.length || 0;
  const corretoresAtivos = corretores?.filter(c => c && c.ativo).length || 0;
  const corretoresInativos = totalCorretores - corretoresAtivos;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Lista de Corretores
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total: {totalCorretores} corretores
            {totalCorretores > 0 && (
              <> • {corretoresAtivos} ativos {corretoresInativos > 0 && `• ${corretoresInativos} inativos`}</>
            )}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Adicionar Corretor
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Foto</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>CRECI</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!corretores || corretores.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhum corretor cadastrado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              corretores.map((corretor) => (
                <TableRow key={corretor.id} hover>
                  <TableCell>
                    <Avatar 
                      src={(() => {
                        const imageUrl = getImageUrl(corretor.foto);
                        // Debug: log when foto exists but URL is null
                        if (corretor.foto && !imageUrl) {
                          console.warn(`Foto não pôde ser processada para ${corretor.nome}:`, {
                            foto: typeof corretor.foto === 'string' ? corretor.foto.substring(0, 50) + '...' : corretor.foto,
                            tipo: typeof corretor.foto
                          });
                        }
                        return imageUrl || undefined;
                      })()} 
                      alt={corretor.nome} 
                      sx={{ width: 40, height: 40 }}
                      imgProps={{
                        onError: (e) => {
                          console.error(`Erro ao carregar foto de ${corretor.nome}:`, {
                            src: e.target.src,
                            foto: corretor.foto ? (typeof corretor.foto === 'string' ? corretor.foto.substring(0, 50) + '...' : 'File object') : 'null'
                          });
                          // Se a imagem falhar ao carregar, esconde o src para mostrar as iniciais
                          e.target.style.display = 'none';
                        },
                        onLoad: () => {
                          console.log(`Foto carregada com sucesso para ${corretor.nome}`);
                        }
                      }}
                    >
                      {!corretor.foto && corretor.nome 
                        ? corretor.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                        : ''}
                    </Avatar>
                  </TableCell>
                  <TableCell>{corretor.nome}</TableCell>
                  <TableCell>{corretor.email}</TableCell>
                  <TableCell>{corretor.telefone || '-'}</TableCell>
                  <TableCell>{corretor.creci || '-'}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: corretor.ativo ? 'success.main' : 'text.secondary',
                        fontWeight: corretor.ativo ? 'bold' : 'normal',
                      }}
                    >
                      {corretor.ativo ? 'Ativo' : 'Inativo'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditarCorretor(corretor)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleConfirmarRemover(corretor)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para Adicionar */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 2,
            pt: 3,
            px: 3,
            background: 'linear-gradient(to right, #1a1a1a 0%, #2d2d2d 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              p: 0.75, 
              borderRadius: 1, 
              bgcolor: 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
            }}>
              <AddIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
              Novo Corretor
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpenDialog(false)}
            size="small"
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
            disabled={saving}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: '#1a1a1a' }}>
          <Grid container spacing={3}>
            {/* Coluna Esquerda - Campos */}
            <Grid item xs={12} md={7}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mt:3,
                  borderRadius: 2,
                  bgcolor: '#2d2d2d',
                  border: '1px solid #2D2D2D',
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 3, 
                    fontWeight: 600, 
                    color: '#ffffff',
                    fontSize: '0.95rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Informações Básicas
                </Typography>
                <TextField
                  fullWidth
                  label="Nome Completo"
                  variant="outlined"
                  value={novoCorretor.nome}
                  onChange={(e) => setNovoCorretor({ ...novoCorretor, nome: e.target.value })}
                  required
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ fontSize: 18, color: '#bdbdbd' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#2d2d2d',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#2d2d2d',
                      },
                      '& fieldset': {
                        borderColor: '#424242',
                      },
                      '&:hover fieldset': {
                        borderColor: '#757575',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#757575',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#bdbdbd',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ffffff',
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  variant="outlined"
                  value={novoCorretor.email}
                  onChange={(e) => setNovoCorretor({ ...novoCorretor, email: e.target.value })}
                  required
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ fontSize: 18, color: '#bdbdbd' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#2d2d2d',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#2d2d2d',
                      },
                      '& fieldset': {
                        borderColor: '#424242',
                      },
                      '&:hover fieldset': {
                        borderColor: '#757575',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#757575',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#bdbdbd',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ffffff',
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Telefone"
                  variant="outlined"
                  value={novoCorretor.telefone}
                  onChange={(e) => setNovoCorretor({ ...novoCorretor, telefone: e.target.value })}
                  placeholder="(51) 99999-9999"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ fontSize: 18, color: '#bdbdbd' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#2d2d2d',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#2d2d2d',
                      },
                      '& fieldset': {
                        borderColor: '#424242',
                      },
                      '&:hover fieldset': {
                        borderColor: '#757575',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#757575',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#bdbdbd',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ffffff',
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="CRECI"
                  variant="outlined"
                  value={novoCorretor.creci}
                  onChange={(e) => setNovoCorretor({ ...novoCorretor, creci: e.target.value })}
                  placeholder="Ex: 102.891E"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon sx={{ fontSize: 18, color: '#bdbdbd' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#2d2d2d',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#2d2d2d',
                      },
                      '& fieldset': {
                        borderColor: '#424242',
                      },
                      '&:hover fieldset': {
                        borderColor: '#757575',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#757575',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#bdbdbd',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ffffff',
                    }
                  }}
                />
                {/* Switch Ativo */}
          <Box sx={{ mt: 3 }}>
          <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 600, 
                    color: '#ffffff',
                    fontSize: '0.95rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Status do Corretor
                </Typography>
            <Paper 
              elevation={0} 
              sx={{ 
                bgcolor: '#2d2d2d',
              }}
            >
              
              <FormControlLabel
                control={
                  <Switch
                    checked={novoCorretor.ativo}
                    onChange={(e) => setNovoCorretor({ ...novoCorretor, ativo: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#ffffff',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#757575',
                      },
                      '& .MuiSwitch-track': {
                        bgcolor: '#424242',
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
                    Corretor Ativo
                  </Typography>
                }
              />
            </Paper>
          </Box>
              </Paper>
            </Grid>
            
            {/* Coluna Direita - Foto */}
            <Grid item xs={12} md={5}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  bgcolor: '#2d2d2d',
                  border: '1px solid #424242',
                  height: '93%',
                  mt:3,
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 3, 
                    fontWeight: 600, 
                    color: '#ffffff',
                    fontSize: '0.95rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Foto do Corretor
                </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="upload-foto-corretor"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const validation = validateImageFile(file);
                  if (!validation.valid) {
                    alert(validation.error);
                    return;
                  }
                  if (novoCorretor.fotoPreview) {
                    URL.revokeObjectURL(novoCorretor.fotoPreview);
                  }
                  const previewUrl = URL.createObjectURL(file);
                  setNovoCorretor({ 
                    ...novoCorretor, 
                    foto: file,
                    fotoPreview: previewUrl
                  });
                }
              }}
            />
            {(novoCorretor.foto || novoCorretor.fotoPreview) ? (
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={novoCorretor.fotoPreview || getImageUrl(novoCorretor.foto) || undefined}
                  alt="Preview"
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    mx: 'auto',
                    mb: 2,
                    border: '3px solid #424242',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                  imgProps={{
                    onError: (e) => {
                      console.error('Erro ao carregar preview da imagem');
                      e.target.style.display = 'none';
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <label htmlFor="upload-foto-corretor">
                    <Button
                      variant="outlined"
                      component="span"
                      size="small"
                      startIcon={<PhotoCameraIcon />}
                      sx={{
                        borderColor: '#757575',
                        color: '#ffffff',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          bgcolor: '#424242',
                        }
                      }}
                    >
                      Alterar
                    </Button>
                  </label>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (novoCorretor.fotoPreview) {
                        URL.revokeObjectURL(novoCorretor.fotoPreview);
                      }
                      setNovoCorretor({ ...novoCorretor, foto: null, fotoPreview: null });
                    }}
                    disabled={uploadingFoto}
                    sx={{
                      borderColor: '#bdbdbd',
                      color: '#ffffff',
                      '&:hover': {
                        borderColor: '#9e9e9e',
                        bgcolor: '#f5f5f5',
                        color: '#2d2d2d',
                      }
                    }}
                  >
                    Remover
                  </Button>
                </Box>
              </Box>
            ) : (
              <label htmlFor="upload-foto-corretor">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<PhotoCameraIcon />}
                  sx={{
                    py: 2.5,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: '#757575',
                    color: '#bdbdbd',
                    bgcolor: '#1a1a1a',
                    '&:hover': {
                      borderColor: '#bdbdbd',
                      bgcolor: '#2d2d2d',
                      borderStyle: 'dashed',
                      borderWidth: 2,
                    }
                  }}
                >
                  Selecionar Foto
                </Button>
              </label>
            )}
              </Paper>
            </Grid>
          </Grid>
          
          
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#1a1a1a', borderTop: '1px solid #424242' }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            disabled={saving}
            sx={{
              color: '#bdbdbd',
              '&:hover': {
                bgcolor: '#2d2d2d',
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleAdicionarCorretor} 
            variant="contained"
            disabled={!novoCorretor.nome || !novoCorretor.email || saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            sx={{
              bgcolor: '#ffffff',
              color: '#1a1a1a',
              px: 3,
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
              '&:disabled': {
                bgcolor: '#424242',
                color: '#757575',
              }
            }}
          >
            {saving ? 'Adicionando...' : 'Adicionar Corretor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Editar */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 2,
            pt: 3,
            px: 3,
            background: 'linear-gradient(to right, #1a1a1a 0%, #2d2d2d 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              p: 0.75, 
              borderRadius: 1, 
              bgcolor: 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
            }}>
              <EditIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
              Editar Corretor
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              if (!saving) {
                setOpenEditDialog(false);
                setCorretorSelecionado(null);
                setNovoCorretor({
                  nome: '',
                  email: '',
                  telefone: '',
                  creci: '',
                  foto: null,
                  fotoPreview: null,
                  ativo: true,
                });
              }
            }}
            size="small"
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
            disabled={saving}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: '#1a1a1a' }}>
          <Grid container spacing={3}>
            {/* Coluna Esquerda - Campos */}
            <Grid item xs={12} md={7}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mt: 3,
                  borderRadius: 2,
                  bgcolor: '#2d2d2d',
                  border: '1px solid #2D2D2D',
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 3, 
                    fontWeight: 600, 
                    color: '#ffffff',
                    fontSize: '0.95rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Informações Básicas
                </Typography>
                <TextField
                  fullWidth
                  label="Nome Completo"
                  variant="outlined"
                  value={novoCorretor.nome}
                  onChange={(e) => setNovoCorretor({ ...novoCorretor, nome: e.target.value })}
                  required
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ fontSize: 18, color: '#bdbdbd' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#2d2d2d',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#2d2d2d',
                      },
                      '& fieldset': {
                        borderColor: '#424242',
                      },
                      '&:hover fieldset': {
                        borderColor: '#757575',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#757575',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#bdbdbd',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ffffff',
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  variant="outlined"
                  value={novoCorretor.email}
                  onChange={(e) => setNovoCorretor({ ...novoCorretor, email: e.target.value })}
                  required
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ fontSize: 18, color: '#bdbdbd' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#2d2d2d',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#2d2d2d',
                      },
                      '& fieldset': {
                        borderColor: '#424242',
                      },
                      '&:hover fieldset': {
                        borderColor: '#757575',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#757575',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#bdbdbd',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ffffff',
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Telefone"
                  variant="outlined"
                  value={novoCorretor.telefone}
                  onChange={(e) => setNovoCorretor({ ...novoCorretor, telefone: e.target.value })}
                  placeholder="(51) 99999-9999"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ fontSize: 18, color: '#bdbdbd' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#2d2d2d',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#2d2d2d',
                      },
                      '& fieldset': {
                        borderColor: '#424242',
                      },
                      '&:hover fieldset': {
                        borderColor: '#757575',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#757575',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#bdbdbd',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ffffff',
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="CRECI"
                  variant="outlined"
                  value={novoCorretor.creci}
                  onChange={(e) => setNovoCorretor({ ...novoCorretor, creci: e.target.value })}
                  placeholder="Ex: 102.891E"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon sx={{ fontSize: 18, color: '#bdbdbd' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#2d2d2d',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#2d2d2d',
                      },
                      '& fieldset': {
                        borderColor: '#424242',
                      },
                      '&:hover fieldset': {
                        borderColor: '#757575',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#757575',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#bdbdbd',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ffffff',
                    }
                  }}
                />
                {/* Switch Ativo */}
                <Box sx={{ mt: 3 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 1, 
                      fontWeight: 600, 
                      color: '#ffffff',
                      fontSize: '0.95rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Status do Corretor
                  </Typography>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      bgcolor: '#2d2d2d',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={novoCorretor.ativo}
                          onChange={(e) => setNovoCorretor({ ...novoCorretor, ativo: e.target.checked })}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4caf50',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              bgcolor: '#4caf50',
                            },
                            '& .MuiSwitch-track': {
                              bgcolor: '#757575',
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
                          Corretor Ativo
                        </Typography>
                      }
                    />
                  </Paper>
                </Box>
              </Paper>
            </Grid>
            
            {/* Coluna Direita - Foto */}
            <Grid item xs={12} md={5}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  bgcolor: '#2d2d2d',
                  border: '1px solid #424242',
                  height: '93%',
                  mt: 3,
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 3, 
                    fontWeight: 600, 
                    color: '#ffffff',
                    fontSize: '0.95rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Foto do Corretor
                </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="upload-foto-corretor-edit"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const validation = validateImageFile(file);
                  if (!validation.valid) {
                    alert(validation.error);
                    return;
                  }
                  if (novoCorretor.fotoPreview) {
                    URL.revokeObjectURL(novoCorretor.fotoPreview);
                  }
                  const previewUrl = URL.createObjectURL(file);
                  setNovoCorretor({ 
                    ...novoCorretor, 
                    foto: file,
                    fotoPreview: previewUrl
                  });
                }
              }}
            />
            {(novoCorretor.foto || novoCorretor.fotoPreview || (corretorSelecionado?.foto && !novoCorretor.fotoPreview)) ? (
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={novoCorretor.fotoPreview || getImageUrl(novoCorretor.foto || corretorSelecionado?.foto) || undefined}
                  alt="Preview"
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    mx: 'auto',
                    mb: 2,
                    border: '3px solid #424242',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                  imgProps={{
                    onError: (e) => {
                      console.error('Erro ao carregar preview da imagem');
                      e.target.style.display = 'none';
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <label htmlFor="upload-foto-corretor-edit">
                    <Button
                      variant="outlined"
                      component="span"
                      size="small"
                      startIcon={<PhotoCameraIcon />}
                      sx={{
                        borderColor: '#757575',
                        color: '#ffffff',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          bgcolor: '#424242',
                        }
                      }}
                    >
                      Alterar
                    </Button>
                  </label>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (novoCorretor.fotoPreview) {
                        URL.revokeObjectURL(novoCorretor.fotoPreview);
                      }
                      setNovoCorretor({ ...novoCorretor, foto: null, fotoPreview: null });
                    }}
                    disabled={uploadingFoto}
                    sx={{
                      borderColor: '#bdbdbd',
                      color: '#fff',
                      '&:hover': {
                        borderColor: '#9e9e9e',
                        bgcolor: '#f5f5f5',
                        color: '#2d2d2d',
                      }
                    }}
                  >
                    Remover
                  </Button>
                </Box>
              </Box>
            ) : (
              <label htmlFor="upload-foto-corretor-edit">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<PhotoCameraIcon />}
                  sx={{
                    py: 2.5,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: '#757575',
                    color: '#bdbdbd',
                    bgcolor: '#1a1a1a',
                    '&:hover': {
                      borderColor: '#bdbdbd',
                      bgcolor: '#2d2d2d',
                      borderStyle: 'dashed',
                      borderWidth: 2,
                    }
                  }}
                >
                  Selecionar Foto
                </Button>
              </label>
              )}
              </Paper>
            </Grid>
          </Grid>
          
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#1a1a1a', borderTop: '1px solid #424242' }}>
          <Button 
            onClick={() => {
              if (!saving) {
                setOpenEditDialog(false);
                setCorretorSelecionado(null);
                setNovoCorretor({
                  nome: '',
                  email: '',
                  telefone: '',
                  creci: '',
                  foto: null,
                  fotoPreview: null,
                  ativo: true,
                });
              }
            }}
            disabled={saving}
            sx={{
              color: '#bdbdbd',
              '&:hover': {
                bgcolor: '#2d2d2d',
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSalvarEdicao} 
            variant="contained"
            disabled={!novoCorretor.nome || !novoCorretor.email || saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              bgcolor: '#ffffff',
              color: '#1a1a1a',
              px: 3,
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
              '&:disabled': {
                bgcolor: '#424242',
                color: '#757575',
              }
            }}
          >
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Confirmar Remoção */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmar Remoção</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover o corretor <strong>{corretorSelecionado?.nome}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Esta ação não pode ser desfeita. Todas as vendas associadas a este corretor também serão removidas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (!deleting) {
                setOpenDeleteDialog(false);
                setCorretorSelecionado(null);
              }
            }}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleRemoverCorretor} 
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {deleting ? 'Removendo...' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ListaCorretores;

